import { CallBackProps, Step } from 'react-joyride';
import { useLocation } from 'react-router-dom';

import { dataCenterSteps } from '@/constants/tour/data-center';
import { financialOverviewSteps } from '@/constants/tour/financial-overview';
import { librarySteps } from '@/constants/tour/library-steps';
import { profileSteps } from '@/constants/tour/profile-steps';
import { territoriesSteps } from '@/constants/tour/territories-tour';
import { zeeProfileSteps } from '@/constants/tour/zee-profile';

import { useDataCenterTour } from './useDataCenterTour';
import { useFinancialOverviewTour } from './useFinancialOverviewTour';
import { useLibraryTour } from './useLibraryTour';
import { useProfileTour } from './useProfileTour';
import { useTerritoriesTour } from './useTerritoriesTour';
import { useZeeProfileTour } from './useZeeProfileTour';

type TourObject = {
  run: boolean;
  stepIndex: number;
  tourCompleted: boolean;
  handleJoyrideCallback: (data: CallBackProps) => void;
  startTour: () => void;
  stopTour: () => void;
  resetTour: () => void;
  goToStep: (step: number) => void;
};

interface TourManagerReturn {
  currentTour: TourObject | null;
  currentSteps: Step[];
  hasTour: boolean;
}

export const useTourManager = (): TourManagerReturn => {
  const { pathname } = useLocation();

  // Initialize all tour hooks
  const dataCenterTour = useDataCenterTour({ steps: dataCenterSteps });
  const libraryTour = useLibraryTour({ steps: librarySteps });
  const territoriesTour = useTerritoriesTour({
    steps: territoriesSteps,
    eventName: 'startTerritoriesTour',
  });
  const financialTour = useFinancialOverviewTour({
    steps: financialOverviewSteps,
  });
  const zeeProfileTour = useZeeProfileTour({
    steps: zeeProfileSteps,
  });
  const profileTour = useProfileTour({
    steps: profileSteps,
  });

  // Determine current tour based on route
  const getCurrentTour = () => {
    if (pathname.includes('/dashboard/data-center')) {
      return { tour: dataCenterTour, steps: dataCenterSteps };
    } else if (pathname.includes('/dashboard/library')) {
      return { tour: libraryTour, steps: librarySteps };
    } else if (pathname.includes('/dashboard/territories')) {
      return { tour: territoriesTour, steps: territoriesSteps };
    } else if (pathname.includes('/dashboard/financial-overview')) {
      return { tour: financialTour, steps: financialOverviewSteps };
    } else if (pathname.includes('/dashboard/zee-profile')) {
      return { tour: zeeProfileTour, steps: zeeProfileSteps };
    } else if (pathname.includes('/dashboard/profile')) {
      return { tour: profileTour, steps: profileSteps };
    }
    return null;
  };

  const currentTourData = getCurrentTour();

  return {
    currentTour: currentTourData?.tour || null,
    currentSteps: currentTourData?.steps || [],
    hasTour: !!currentTourData,
  };
};
